import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import {
  isThraceAdmin,
  resolveThraceNation,
  thraceNationsForUser,
  THRACE_SELECT_CLASS,
} from '../../utils/thraceCountry';

type EpiRow = {
  epiunitID: number;
  districtID: number;
  district_name?: string;
  province_name?: string;
  epiunitcountrycode: string;
  epiunitname: string;
  epiunittype: string;
  risklevel: string;
  villagename: string;
  latit: string;
  longi: string;
  data_source: string;
  _dirty?: boolean;
  _isNew?: boolean;
};

type District = {
  district_id: number;
  district_name: string;
  province_id?: number;
  province_name?: string;
};

const emptyNew = (districtId: number): EpiRow => ({
  epiunitID: 0,
  districtID: districtId,
  epiunitcountrycode: '',
  epiunitname: 'FARM',
  epiunittype: 'F',
  risklevel: 'high',
  villagename: '',
  latit: '',
  longi: '',
  data_source: 'thrace',
  _dirty: true,
  _isNew: true,
});

const ThraceEpiunitsPanel: React.FC = () => {
  const { user } = useAuthStore();
  const nations = useMemo(
    () => thraceNationsForUser(user?.role, user?.country),
    [user?.role, user?.country],
  );
  const admin = isThraceAdmin(user?.role);
  const defaultNation =
    resolveThraceNation(user?.country)?.nationId ?? nations[0]?.nationId ?? 300;
  const [nationId, setNationId] = useState(defaultNation);
  const [rows, setRows] = useState<EpiRow[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [newDistrictId, setNewDistrictId] = useState<number>(0);

  const load = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const [eu, dist] = await Promise.all([
        apiService.thrace.getEpiunits(nationId),
        apiService.thrace.getEpiunitDistricts(nationId),
      ]);
      const list: EpiRow[] = (eu.data?.epiunits || []).map((r: any) => {
        const village = r.villagename || '';
        // BG (and rare others) historically stored blank codes — fall back to village / ID
        const code =
          (r.epiunitcountrycode && String(r.epiunitcountrycode).trim()) ||
          village ||
          (r.epiunitID != null ? String(r.epiunitID) : '');
        return {
          epiunitID: Number(r.epiunitID),
          districtID: Number(r.districtID),
          district_name: r.district_name || '',
          province_name: r.province_name || '',
          epiunitcountrycode: code,
          epiunitname: r.epiunitname || '',
          epiunittype: r.epiunittype || 'F',
          risklevel: (r.risklevel || 'high').toLowerCase(),
          villagename: village,
          latit: r.latit || '',
          longi: r.longi || '',
          data_source: (r.data_source || 'thrace').toLowerCase(),
        };
      });
      setRows(list);
      const dlist: District[] = dist.data?.districts || [];
      setDistricts(dlist);
      if (dlist.length) setNewDistrictId(Number(dlist[0].district_id));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load epiunits');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationId]);

  const patch = (idx: number, field: keyof EpiRow, value: string | number) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value, _dirty: true } : r)),
    );
  };

  const addRow = () => {
    if (!newDistrictId) {
      setError('Select a district before adding an epiunit.');
      return;
    }
    setRows((prev) => [...prev, emptyNew(newDistrictId)]);
  };

  const saveDirty = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (!dirty.length) {
      setMessage('No changes to save.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    const errors: string[] = [];
    let ok = 0;
    try {
      for (const r of dirty) {
        const payload = {
          nation_id: nationId,
          districtID: r.districtID,
          epiunitcountrycode: r.epiunitcountrycode,
          epiunitname: r.epiunitname,
          epiunittype: r.epiunittype,
          risklevel: r.risklevel,
          villagename: r.villagename,
          latit: r.latit,
          longi: r.longi,
          data_source: r.data_source,
        };
        try {
          if (r._isNew) {
            await apiService.thrace.createEpiunit(payload);
          } else {
            await apiService.thrace.updateEpiunit(r.epiunitID, payload);
          }
          ok += 1;
        } catch (e: any) {
          errors.push(
            `${r.villagename || r.epiunitcountrycode || 'row'}: ${
              e?.response?.data?.detail || e?.message || 'failed'
            }`,
          );
        }
      }
      if (errors.length) setError(errors.join('\n'));
      if (ok) {
        setMessage(`Saved ${ok} epiunit(s).`);
        await load();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        View and edit epiunits for your country. Add new units to an existing Thrace district, then
        save.
      </p>
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
          {admin ? (
            <select
              value={nationId}
              onChange={(e) => setNationId(Number(e.target.value))}
              className={THRACE_SELECT_CLASS}
            >
              {nations.map((n) => (
                <option key={n.nationId} value={n.nationId}>
                  {n.label}
                </option>
              ))}
            </select>
          ) : (
            <div className={`${THRACE_SELECT_CLASS} bg-gray-50 text-gray-800`}>
              {nations.find((n) => n.nationId === nationId)?.label || '—'}
            </div>
          )}
        </div>
        <button className="nav-btn" onClick={load} disabled={loading || saving}>
          {loading ? 'Loading…' : 'Reload'}
        </button>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            District for new epiunit
          </label>
          <select
            value={newDistrictId}
            onChange={(e) => setNewDistrictId(Number(e.target.value))}
            className={`${THRACE_SELECT_CLASS} max-w-xs`}
          >
            {districts.map((d) => (
              <option key={d.district_id} value={d.district_id}>
                {d.district_name}
                {d.province_name ? ` (${d.province_name})` : ''}
              </option>
            ))}
          </select>
        </div>
        <button className="nav-btn" onClick={addRow} disabled={saving || !districts.length}>
          Add epiunit
        </button>
        <button className="nav-btn" onClick={saveDirty} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm whitespace-pre-wrap">
          {error}
        </div>
      )}
      {message && (
        <div className="p-3 bg-green-50 border border-green-200 text-green-800 rounded text-sm">
          {message}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[70vh]">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left px-2 py-2">ID</th>
              <th className="text-left px-2 py-2">District</th>
              <th className="text-left px-2 py-2">Code</th>
              <th className="text-left px-2 py-2">Village</th>
              <th className="text-left px-2 py-2">Name</th>
              <th className="text-left px-2 py-2">Type</th>
              <th className="text-left px-2 py-2">Risk</th>
              <th className="text-left px-2 py-2">Source</th>
              <th className="text-left px-2 py-2">Lat</th>
              <th className="text-left px-2 py-2">Lon</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={r._isNew ? `new-${idx}` : r.epiunitID}
                className={`border-t border-gray-100 ${r._dirty ? 'bg-amber-50' : ''}`}
              >
                <td className="px-2 py-1 text-gray-500">{r._isNew ? 'new' : r.epiunitID}</td>
                <td className="px-2 py-1 whitespace-nowrap">{r.district_name || r.districtID}</td>
                <td className="px-1 py-1">
                  <input
                    value={r.epiunitcountrycode}
                    onChange={(e) => patch(idx, 'epiunitcountrycode', e.target.value)}
                    className="w-28 px-1 py-0.5 border border-gray-200 rounded"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    value={r.villagename}
                    onChange={(e) => patch(idx, 'villagename', e.target.value)}
                    className="w-36 px-1 py-0.5 border border-gray-200 rounded"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    value={r.epiunitname}
                    onChange={(e) => patch(idx, 'epiunitname', e.target.value)}
                    className="w-24 px-1 py-0.5 border border-gray-200 rounded"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    value={r.epiunittype}
                    onChange={(e) => patch(idx, 'epiunittype', e.target.value)}
                    className="w-12 px-1 py-0.5 border border-gray-200 rounded"
                    maxLength={1}
                  />
                </td>
                <td className="px-1 py-1">
                  <select
                    value={r.risklevel}
                    onChange={(e) => patch(idx, 'risklevel', e.target.value)}
                    className="px-1 py-0.5 border border-gray-200 rounded"
                  >
                    <option value="high">high</option>
                    <option value="low">low</option>
                  </select>
                </td>
                <td className="px-1 py-1">
                  <select
                    value={r.data_source}
                    onChange={(e) => patch(idx, 'data_source', e.target.value)}
                    className="px-1 py-0.5 border border-gray-200 rounded"
                  >
                    <option value="thrace">thrace</option>
                    <option value="tads">tads</option>
                  </select>
                </td>
                <td className="px-1 py-1">
                  <input
                    value={r.latit}
                    onChange={(e) => patch(idx, 'latit', e.target.value)}
                    className="w-20 px-1 py-0.5 border border-gray-200 rounded"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    value={r.longi}
                    onChange={(e) => patch(idx, 'longi', e.target.value)}
                    className="w-20 px-1 py-0.5 border border-gray-200 rounded"
                  />
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                  No epiunits loaded for this country.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ThraceEpiunitsPanel;
